import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [navigationResetCallback, setNavigationResetCallback] = useState(null);

  // Check if user is already logged in on app start
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    console.log('AuthContext: checkAuth started');
    try {
      setIsLoading(true);
      
      // Set a timeout for auth check to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Auth check timeout')), 10000)
      );
      
      const isValid = await Promise.race([
        authService.validateToken(),
        timeoutPromise
      ]);
      
      console.log('AuthContext: validateToken result:', isValid);
      
      if (isValid) {
        const userData = await authService.getUserData();
        console.log('AuthContext: userData:', userData);
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);
        }
      } else {
        await authService.logout();
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // Clear auth state on any error (including timeout)
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      console.log('AuthContext: checkAuth finished, setting isLoading=false');
      setIsLoading(false);
    }
  };

  const login = async (mobileNumber, countryCode, password) => {
    try {
      const result = await authService.login(mobileNumber, countryCode, password);
      
      if (result.success) {
        const userData = await authService.getUserData();
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true, data: result.data };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      
      // Reset navigation to Home and clear stack
      if (navigationResetCallback) {
        navigationResetCallback();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const register = async (userData, type = 'member') => {
    try {
      const result = type === 'mediator' 
        ? await authService.registerMediator(userData)
        : await authService.registerMember(userData);
      
      return result;
    } catch (error) {
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    register,
    checkAuth,
    setNavigationResetCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
